import React, { useEffect, useState } from "react";
import { api, toggleWatchlist, getUserToken } from "../lib/api";
import { Sparkle, Trophy, Plus, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ParlayCard({ onAddedToWatchlist }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingAll, setAddingAll] = useState(false);
  const token = getUserToken();

  useEffect(() => {
    let cancelled = false;
    api
      .get("/parlay/today")
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const addAllToPicks = async () => {
    if (!data?.legs?.length) return;
    setAddingAll(true);
    let added = 0;
    for (const leg of data.legs) {
      try {
        const res = await toggleWatchlist(token, leg.id);
        if (res && res.status === "added") added += 1;
      } catch (e) {
        console.debug("watchlist add failed for", leg.id, e);
      }
    }
    setAddingAll(false);
    toast("Added to My Picks", {
      description: `${added} of ${data.legs.length} legs added to your list.`,
    });
    if (onAddedToWatchlist) onAddedToWatchlist();
  };

  if (loading) return null;
  if (!data || data.leg_count < 2) return null; // Only show when 2+ legs pinned

  const positiveAmerican = data.combined_american > 0;

  return (
    <section
      className="border-2 relative overflow-hidden mb-8"
      style={{
        borderColor: "#FF4500",
        background: "linear-gradient(135deg, rgba(255,69,0,0.08) 0%, rgba(10,10,10,0) 60%)",
      }}
      data-testid="creator-parlay-card"
    >
      {/* Header */}
      <header className="flex items-center justify-between px-5 py-3 border-b border-[#27272A]">
        <div className="flex items-center gap-2">
          <Sparkle size={18} weight="fill" style={{ color: "#FF4500" }} />
          <div>
            <div
              className="overline"
              style={{ color: "#FF4500", letterSpacing: "0.14em" }}
              data-testid="parlay-title"
            >
              CREATOR&apos;S PARLAY OF THE DAY
            </div>
            <div className="text-xs mt-0.5" style={{ color: "#71717A" }}>
              {data.leg_count} legs · hand-picked by GP
            </div>
          </div>
        </div>
        <div className="text-right">
          <div
            className="font-data text-2xl"
            style={{ color: positiveAmerican ? "#22c55e" : "#fff" }}
            data-testid="parlay-combined-american"
          >
            {positiveAmerican ? "+" : ""}
            {data.combined_american}
          </div>
          <div className="overline">Combined Odds</div>
        </div>
      </header>

      {/* Legs */}
      <ul className="divide-y divide-[#27272A]" data-testid="parlay-legs-list">
        {data.legs.map((leg, i) => (
          <li
            key={leg.id}
            className="px-5 py-3 flex items-center justify-between gap-3"
            data-testid={`parlay-leg-${i}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div
                className="font-data text-xs w-6 h-6 flex items-center justify-center border border-[#27272A]"
                style={{ color: "#71717A" }}
              >
                {i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" data-testid={`parlay-leg-${i}-title`}>
                  {leg.player?.name || "—"}
                </div>
                <div className="text-xs mt-0.5" style={{ color: "#A1A1AA" }}>
                  {leg.stat_type} {leg.pick} {leg.line}
                  {leg.bookmaker_title ? ` · ${leg.bookmaker_title}` : ""}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-data text-sm">
                {leg.price > 0 ? "+" : ""}
                {leg.price ?? "—"}
              </div>
              <div className="overline">{leg.tier}</div>
            </div>
          </li>
        ))}
      </ul>

      {/* Payout table */}
      {data.payouts && (
        <div className="grid grid-cols-3 gap-px bg-[#27272A]" data-testid="parlay-payouts-grid">
          <PayoutCell stake="$10" payout={data.payouts["10"]} />
          <PayoutCell stake="$25" payout={data.payouts["25"]} />
          <PayoutCell stake="$100" payout={data.payouts["100"]} highlight />
        </div>
      )}

      {/* Actions */}
      <div className="px-5 py-4 border-t border-[#27272A] flex items-center justify-between gap-3">
        <div className="text-xs" style={{ color: "#71717A" }}>
          <Trophy size={12} weight="fill" style={{ display: "inline", marginRight: 4, color: "#FACC15" }} />
          All legs must hit to win. Odds are indicative — verify on your book before betting.
        </div>
        <button
          type="button"
          onClick={addAllToPicks}
          disabled={addingAll}
          className="flex items-center gap-2 px-4 py-2 border-2 transition-all disabled:opacity-50"
          style={{
            borderColor: "#FF4500",
            color: "#FF4500",
            background: "transparent",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#FF4500";
            e.currentTarget.style.color = "#0A0A0A";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#FF4500";
          }}
          data-testid="parlay-add-all-btn"
        >
          {addingAll ? <span className="overline">Adding…</span> : (
            <>
              <Plus size={14} weight="bold" />
              <span className="overline" style={{ color: "inherit" }}>Add All to My Picks</span>
              <ArrowRight size={14} weight="bold" />
            </>
          )}
        </button>
      </div>
    </section>
  );
}

function PayoutCell({ stake, payout, highlight }) {
  return (
    <div
      className="bg-[#0A0A0A] p-3 text-center"
      style={highlight ? { background: "rgba(255,69,0,0.06)" } : undefined}
      data-testid={`parlay-payout-${stake.replace("$", "")}`}
    >
      <div className="overline">{stake} bet</div>
      <div
        className="font-data text-lg mt-1"
        style={{ color: highlight ? "#FF4500" : "#fff" }}
      >
        ${payout}
      </div>
    </div>
  );
}
